import type { TossPaymentsWidgets, WidgetPaymentMethodWidget } from "@tosspayments/tosspayments-sdk";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { t } from "i18next";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IMetaobjectQuery } from "@repo/shopify/types/api/metaobject";
import type { MoneyV2 } from "@repo/shopify/types/generated/storefront.types";

import { fetchOneTimePayment } from "@api/lambdaApi";

import Button from "@atoms/Button";
import Loading from "@atoms/Loading";
import Price from "@atoms/Price";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useMetaObjects from "@queries/useMetaObjects";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

const OTP_TINT_INTL_PAY_PARAM = "OTP_TINT_INTL_PAY_PARAM";

const CLIENT_KEY = import.meta.env.VITE_REACT_APP_TOSS_CLIENT_KEY as string;

export default function CheckoutPaymentIntl() {
  const { handle } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { customer } = useCustomerStore();
  const { openModal, openToast } = useLayoutStore();
  const { isPC } = useCheckDevice();

  const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);
  const [isPaymentWidgetLoading, setIsPaymentWidgetLoading] = useState(false);

  const paymentWidgetRef = useRef<TossPaymentsWidgets | null>(null);
  const paymentMethodsWidgetRef = useRef<WidgetPaymentMethodWidget | null>(null);

  const { fetchMetaObject, metaObjectData: data, metaObjectLoading } = useMetaObjects();

  const paymentData = data?.metaobjects?.edges.find(({ node }) => node.handle === handle);
  const isPaid = JSON.parse(paymentData?.node?.fields?.find((f) => f.key === "is_paid")?.value || "false") as boolean;
  const money = JSON.parse(paymentData?.node?.fields?.find((f) => f.key === "ramainder")?.value || "null") as MoneyV2;
  const orderNumber = paymentData?.node?.fields?.find((f) => f.key === "order_number")?.value || "";

  useEffect(() => {
    if (isPC) {
      document.body.setAttribute("class", "checkout");
    } else {
      document.body.removeAttribute("class");
    }

    return () => {
      document.body.removeAttribute("class");
    };
  }, [isPC]);

  const init = async () => {
    try {
      const isSearchParamsHandled = await handleSearchParams();
      if (isSearchParamsHandled) return;

      if (!paymentData) {
        openToast({ message: t("necessary_payment_data") });
        navigate(URLS.HOME);
        return;
      }

      if (isPaid) {
        openModal({
          title: "Payment Completed",
          content: <p>Payment is completed.</p>,
          confirm: {
            label: "Okay",
            onClick: () => {
              openModal(null);
              navigate(URLS.HOME);
            },
          },
          onClickClose() {
            navigate(URLS.HOME);
          },
        });
        return;
      }

      await initializeTossWidget();
    } catch (error) {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      navigate(URLS.HOME);
      const message = error instanceof Error ? error.message : String(error);
      console.log(message);
    }
  };

  const handleSearchParams = async () => {
    const orderId = searchParams.get("orderId");
    const paymentKey = searchParams.get("paymentKey");
    const amount = searchParams.get("amount");
    const errorMessage = searchParams.get("message");

    if (errorMessage) {
      setTimeout(() => {
        openValidationModal(errorMessage);
      }, 300);
      return false;
    }

    if (paymentKey && orderId && amount) {
      await completePaymentRequest({ paymentKey, amount, orderId });
      return true;
    }

    return false;
  };

  const initializeTossWidget = async () => {
    if (isPaymentWidgetLoading) return;

    try {
      setIsPaymentWidgetLoading(true);

      const tossPayments = await loadTossPayments(CLIENT_KEY);
      const widgets = tossPayments.widgets({ customerKey: new Date().getTime().toString() });
      await widgets.setAmount({ value: Number(money.amount), currency: "USD" });
      paymentWidgetRef.current = widgets;
      paymentMethodsWidgetRef.current = await widgets.renderPaymentMethods({
        selector: "#payment-method",
        variantKey: "TNT_INTL_PayPal",
      });
    } catch (error) {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      paymentWidgetRef.current = null;
      paymentMethodsWidgetRef.current = null;
      const message = error instanceof Error ? error.message : String(error);
      console.log(message);
    } finally {
      setIsPaymentWidgetLoading(false);
    }
  };

  const requestPaymentWidget = async () => {
    const paymentParam = {
      storeName: "thisisneverthat-intl",
      paymentMethod: "paypal",
      metaobjectId: paymentData?.node.id || "",
      metaobjectHandle: paymentData?.node.handle || "",
    };

    try {
      sessionStorage.setItem(OTP_TINT_INTL_PAY_PARAM, JSON.stringify(paymentParam));
      await paymentWidgetRef.current?.requestPayment({
        orderId: handle || "",
        orderName: `${orderNumber} 추가 결제`,
        customerName: customer.email || "",
        customerEmail: customer.email || "",
        successUrl: window.location.href,
        failUrl: window.location.href,
        metadata: { oneTimePayment: "true" },
      });
    } catch (error: any) {
      if (error.code === "USER_CANCEL") {
        openModal({
          title: "Payment Canceled",
          content: <p>{t("payment_cancel")}</p>,
          confirm: {
            label: "Okay",
            onClick: () => {
              openModal(null);
            },
          },
        });
      }
      const message = error instanceof Error ? error.message : String(error);
      console.log(message);
    }
  };

  const completePaymentRequest = async ({ paymentKey, orderId, amount }: { paymentKey: string; orderId: string; amount: string }) => {
    setIsPaymentInProgress(true);

    const storedPaymentParam = sessionStorage.getItem(OTP_TINT_INTL_PAY_PARAM);
    const paymentParam = {
      paymentKey,
      orderId,
      amount,
      ...(storedPaymentParam ? JSON.parse(storedPaymentParam) : null),
    } as {
      storeName: string;
      paymentMethod: string;
      amount: string;
      paymentKey: string;
      metaobjectId: string;
      metaobjectHandle: string;
    };

    try {
      const response = await fetchOneTimePayment(paymentParam);
      if (response?.message === "success") {
        setSearchParams({});
        openModal({
          title: "Payment Success",
          content: <p>Your payment was successful.</p>,
          confirm: {
            label: "Okay",
            onClick() {
              openModal(null);
              navigate(URLS.HOME);
            },
          },
          onClickClose() {
            navigate(URLS.HOME);
          },
        });
      } else {
        openValidationModal(t("payment_failed"));
        void initializeTossWidget();
      }
    } catch (error) {
      openValidationModal(t("payment_failed"));
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    } finally {
      setIsPaymentInProgress(false);
    }
  };

  const openValidationModal = (text: string) => {
    setSearchParams({});

    openModal({
      title: "Payment Error",
      content: <p>{text}</p>,
      confirm: {
        label: "Okay",
        onClick() {
          openModal(null);
        },
      },
    });
  };

  return (
    <>
      {metaObjectLoading || isPaymentInProgress ? (
        <Loading type="page" />
      ) : (
        <>
          <h1 className="text-title">Complete Payment</h1>
          <div className="flex flex-col gap-10 pt-5">
            {/* PAYMENT METHOD */}
            <div className="flex flex-col gap-2.5">
              <h2>Payment Method</h2>
              {isPaymentWidgetLoading ? <Loading type="more" /> : null}
              <div className={isPaymentWidgetLoading ? "hidden" : ""} id="payment-method" />
            </div>
            <div className="flex flex-col gap-2.5">
              <h2>Order Summary</h2>
              <div className="tnt-gap-x grid grid-cols-6 gap-y-2.5">
                <div className="tnt-gap-x col-span-8 grid grid-cols-2">
                  <span>{`Pending payment amount for order #${orderNumber}`}</span>
                  <div className="flex flex-col items-end">
                    <Price
                      className="text-right"
                      compareAtPrice={Number(money?.amount || "0")}
                      direction="vertical"
                      price={Number(money?.amount || "0")}
                      variant="secondary"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="tnt-gap-x grid grid-cols-6 justify-end gap-y-2.5">
              <div className="pc:col-span-2 pc:col-start-5 col-span-4 col-start-3 flex flex-col gap-1">
                <div className="tnt-gap-x grid grid-cols-2">
                  <span>Total</span>
                  <Price className="justify-end" price={Number(money?.amount || "0")} />
                </div>
              </div>
              <Button
                as="square"
                className="pc:col-span-2 pc:col-start-5 col-span-4 col-start-3 w-full justify-center"
                onClick={requestPaymentWidget}
              >
                {t("checkout.pay_now")}
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
